import { t, tryT, type MessageKey } from '@reos/i18n';
import { INTAKE_FIELD_DEFS, type IntakeFieldView } from './fields.js';

/**
 * Krok P8F7 (nalez P8-N8) — fakty `condition`/`ownership_type` nesou ciselnik
 * (`good`, `personal`…), ne ceskou vetu. Preklad je TYZ katalog, ze ktereho
 * stejne kody cte inzerat (`listingDraft.value.condition/ownership.*`,
 * `apps/web-admin/src/lib/listing-draft-format.ts` `formatValue`) — jeden
 * zdroj pro checklist, rozbor naboru i (zvlast, kvuli deleni katalogu podle
 * stranek) verejnou stranku (`apps/web-public/.../nemovitost.tsx`).
 */
const ENUM_FACT_CATALOG: Readonly<Record<string, string>> = {
  condition: 'listingDraft.value.condition',
  ownership_type: 'listingDraft.value.ownership',
  /** Krok ZAK1b: `utilities_available` nese kody siti (`water`, `sewer`…). */
  utilities_available: 'webAdmin.fieldIntake.utility',
};

/**
 * Krok UXV4c — popisky poli a lidsky zapis hodnot (predloha 8: „žádné kódy,
 * anglické popisky ani syrový výstup AI"). Popisek pole je TYZ katalogovy
 * klic jako v detailu nemovitosti (`property.fact.*`) — jeden zdroj.
 */

export function fieldLabel(factKey: string): string {
  return t(`property.fact.${factKey}` as MessageKey, { locale: 'cs' });
}

export function fieldDef(factKey: string): IntakeFieldView | undefined {
  return INTAKE_FIELD_DEFS.find((f) => f.factKey === factKey);
}

const NUMBER = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 1 });
const CZK = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 });

export function formatValue(factKey: string, value: unknown): string {
  const def = fieldDef(factKey);
  if (typeof value === 'boolean') return t(value ? 'webAdmin.fieldIntake.value.yes' : 'webAdmin.fieldIntake.value.no', { locale: 'cs' });
  if (typeof value === 'number') {
    // Uredni udaje mimo seznam naboru (zastavena plocha z katastru) nesou
    // jednotku v nazvu klice registru (`*_m2`) — stejna konvence jako FACT_KEYS.
    const m2 = def?.unit === 'm2' || factKey.endsWith('_m2');
    return m2 ? `${NUMBER.format(value)} m²` : NUMBER.format(value);
  }
  if (typeof value === 'object' && value !== null && 'amount' in value) {
    const amount = Number((value as { amount: unknown }).amount);
    return Number.isFinite(amount) ? CZK.format(amount) : String((value as { amount: unknown }).amount);
  }
  if (typeof value === 'string') {
    const prefix = ENUM_FACT_CATALOG[factKey];
    if (prefix !== undefined) return tryT(`${prefix}.${value}` as MessageKey, { locale: 'cs' }) ?? value;
    return value;
  }
  /*
   * Krok ZAK1b bod 2 — SEZNAM A SEZNAM POLOZEK. Registr zna typy `list`
   * (sitě, ochranna pasma) a `object_list` (parcely, mistnosti); do ZAK1b je
   * tahle funkce vracela `JSON.stringify`, takze karta zakazky by u parcel
   * ukazala `[{"number":"38/13",…}]`. Polozky se spojuji tecku-mezerou, tedy
   * tymz oddelovacem, jaky uz pouziva karta „Údaje nemovitosti".
   */
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'object' && item !== null && !Array.isArray(item)
          ? Object.entries(item as Record<string, unknown>)
              .map(([field, raw]) =>
                typeof raw === 'number' && field.endsWith('_m2')
                  ? `${NUMBER.format(raw)} m²`
                  : typeof raw === 'number'
                    ? NUMBER.format(raw)
                    : String(raw),
              )
              .join(', ')
          : formatValue(factKey, item),
      )
      .join(' · ');
  }
  return JSON.stringify(value);
}

/**
 * Krok NAB1 — nabidka pro rychle zadani dispozice pri odskrtnuti bodu.
 *
 * Jsou to HODNOTY faktu `disposition`, ne texty UI: registr FACT_KEYS u toho
 * klice rika „czech convention (e.g. 3+1, 2+kk, 5+1)", takze zapis je stejny
 * v kazdem jazyce rozhrani (jako cislo nebo mena). Cokoli mimo nabidku jde
 * napsat rucne do pole vedle.
 */
export const DISPOSITION_CHOICES: readonly string[] = ['1+kk', '1+1', '2+kk', '2+1', '3+kk', '3+1', '4+kk', '4+1', '5+kk', '5+1'];

/** Rucni zadani -> hodnota podle typu pole (nebo `null`, kdyz nesedi). */
export function parseManualValue(factKey: string, raw: string): unknown {
  const def = fieldDef(factKey);
  const text = raw.trim();
  if (def === undefined || text === '') return null;
  switch (def.valueType) {
    case 'number': {
      const n = Number(text.replace(/\s/g, '').replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    case 'integer': {
      const n = Number(text.replace(/\s/g, ''));
      return Number.isInteger(n) ? n : null;
    }
    case 'boolean':
      return text === 'true' ? true : text === 'false' ? false : null;
    case 'money': {
      const digits = text.replace(/\s/g, '').replace(/kč$/i, '');
      return /^\d+$/.test(digits) ? { amount: digits, currency: 'CZK' } : null;
    }
    default:
      return text;
  }
}

export function formatClock(seconds: number | null): string {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
